import { Alert } from '@mui/material'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { finalizeSubscriptionAfterPayment } from 'api/subscription'
import { STRIPE_KEY } from 'const'
import { SubscriptionPaymentForm } from 'features/Subscription/components/SubscriptionPaymentForm'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { SubscribeResponse, SubscriptionPlanType } from 'types'
import { createPaymentIntent, createPaymentIntentInvited } from '../../api/payment'
import { AuthFormTitle } from '../AuthFormTitle/AuthFormTitle'
import {
  FamilyFormType,
  InviteFamilyFormType,
  normalizePartnerCode,
  PartnerCodeNotice,
} from './types'

interface Props {
  formData: FamilyFormType | InviteFamilyFormType
  inviteToken?: string
  /** Unfunded partner code from the code step — sent for partner attribution. */
  partnerCode?: string
  /** Why the entered partner code still leads to payment. */
  notice?: PartnerCodeNotice | null
  /** Leaves the payment step without buying — nothing is created until the card is submitted. */
  onCancel: () => void
}

function InnerForm({ formData, inviteToken, partnerCode, notice, onCancel }: Props) {
  const navigate = useNavigate()

  const handleSubmit = useCallback(
    (paymentMethodId: string, plan: SubscriptionPlanType) => {
      if (inviteToken) {
        return createPaymentIntentInvited(
          inviteToken,
          formData.firstName,
          formData.lastName,
          formData.myNewPassword,
          paymentMethodId,
          plan,
          formData.email,
          normalizePartnerCode(partnerCode),
        )
      }
      return createPaymentIntent(
        formData.email,
        paymentMethodId,
        'caregiver',
        formData as FamilyFormType,
        plan,
        partnerCode,
      )
    },
    [formData, inviteToken, partnerCode],
  )

  const handleSuccess = useCallback(
    async (response: SubscribeResponse) => {
      try {
        await finalizeSubscriptionAfterPayment(response, formData.email)
      } catch (err) {
        console.error('finalizeSubscriptionAfterPayment failed', err)
      }
      navigate(`${routes.successSubscription}?type=family`)
    },
    [formData.email, navigate],
  )

  const handlePaymentFailed = useCallback(() => {
    navigate(`${routes.successSubscription}?type=family&payment_failed=true`)
  }, [navigate])

  return (
    <SubscriptionPaymentForm
      title={
        <>
          <AuthFormTitle textColor="secondary.main">Select Subscription Plan</AuthFormTitle>
          {notice && <Alert severity={notice.severity}>{notice.message}</Alert>}
        </>
      }
      submitLabel="Submit Payment"
      billingEmail={formData.email}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onPaymentFailed={handlePaymentFailed}
      onCancel={onCancel}
    />
  )
}

const FamilyCardForm = (props: Props) => {
  const stripePromise = useMemo(() => loadStripe(STRIPE_KEY), [])

  return (
    <Elements stripe={stripePromise}>
      <InnerForm {...props} />
    </Elements>
  )
}

export default FamilyCardForm
